// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, BrandModel } from "@insite/client-framework/Types/ApiModels";
import { ProductAddToListLinkStyles } from "@insite/content-library/Components/ProductAddToListLink";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import OverlayBadges from "@pacific/App/Components/Product/OverlayBadge";
import ProductActions, { ProductActionsStyle } from "@pacific/App/Components/Product/ProductActions";
import xUseOverlayBadge from "@pacific/App/Hooks/xUseOverlayBadge";
import productDescriptionVariantStyles from "@pacific/App/SharedStyle/ProductDescriptionStyles";
import ProductPartNumbers from "@pacific/Overrides/Components/ProductPartNumbers";
import { color, ellipsis, mergeWithCss, styledScrollbar, xScreen } from "@pacific/XDL/Utilities";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
    // XNG-Changes
    query?: string;
    doSearch?: () => void;
    products: ProductContextModel[];
    isPriceLoaded?: boolean;
    isInventoryLoaded?: boolean;
    availableForPurchase?: (boolean | null | undefined)[];
    onClickEvent?: (item: string, destination: string) => void;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => ({
    availableForPurchase: props.products.map((productContext: ProductContextModel) =>
        hasEnoughInventory(state, productContext),
    ),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

interface AutoCompleteProductStyles {
    container?: InjectableCss;
    productWrapper?: InjectableCss;
    productContainer?: InjectableCss;
    productInfoContainer?: InjectableCss;
    productImage?: ProductImageStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    actionsContainer?: InjectableCss;
    signInButton?: ButtonPresentationProps;
    addToListLink?: ProductAddToListLinkStyles;
    productBrandStyles?: ProductBrandStyles;
    productActionsStyles?: ProductActionsStyle;
    priceWrapper?: InjectableCss;
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
    // XNG-Changes
    titleWrapper?: InjectableCss;
    viewAllLink?: LinkPresentationProps;
    productStyles?: AutoCompleteProductStyles;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        size: 15,
        weight: 500,
        ellipsis: true,
        css: css`
            text-align: left;
            letter-spacing: 0;
            line-height: 24px;
            margin-right: 10px;
            & > ${TypographyStyle} {
                margin-bottom: 0;
            }
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            flex-wrap: wrap;
            padding-bottom: 14px;
            margin-bottom: 10px;
            padding-right: 16px;
            border-bottom: 1px solid ${color("common.border")};
            ${xScreen("lte", "md")} {
                border-top: 1px solid ${color("common.border")};
                padding-top: 20px;
            }
        `,
    },
    viewAllLink: {
        typographyProps: {
            size: 12,
            weight: 600,
            css: css`
                letter-spacing: 0;
                line-height: 17px;
            `,
        },
    },
    productStyles: {
        container: {
            css: css`
                /* XNGAGE Changes: remove max-height: 420px; */
                padding-right: 8px;
                ${xScreen("lte", "md")} {
                    width: 100%;
                    max-height: initial;
                }
                ${xScreen("gt", "md")} {
                    ${styledScrollbar(4)}
                }
            `,
        },
        productWrapper: {
            css: css`
                display: flex;
                padding-bottom: 12px;
                flex-wrap: nowrap;

                &:not(.last-item) {
                    border-bottom: 1px solid ${color("common.border")};
                    margin-bottom: 12px;
                }
            `,
        },
        productDescriptionStyles: {
            productDetailLink: {
                ...mergeWithCss(productDescriptionVariantStyles?.small?.productDetailLink, {
                    css: css`
                        margin-bottom: 4px;
                        ${ellipsis(2)}
                    `,
                }),
            },
            productDetailDescription: {
                ...mergeWithCss(productDescriptionVariantStyles?.small?.productDetailDescription, {
                    css: css`
                        margin-bottom: 4px;
                        ${ellipsis(2)}
                    `,
                }),
            },
        },
        productContainer: {
            css: css`
                flex-grow: 1;
                display: flex;
                flex-wrap: wrap;
                ${xScreen("lte", "sm")} {
                    flex-direction: column;
                }
            `,
        },
        productInfoContainer: {
            css: css`
                display: flex;
                flex-grow: 1;
                flex-direction: column;
                margin-right: 32px;
                max-width: 285px;
                ${xScreen("lte", "xs")} {
                    margin-right: 0;
                    margin-bottom: 12px;
                }
            `,
        },
        priceWrapper: {
            css: css`
                margin-bottom: 3px;
            `,
        },
        actionsContainer: {
            css: css`
                flex-grow: 1;
                ${xScreen("lte", "xs")} {
                    & {
                        max-width: unset;
                        width: 100%;
                    }
                }
            `,
        },
        productImage: {
            linkWrappingImage: {
                css: css`
                    height: 86px;
                    width: 86px;
                    margin-right: 16px;
                    flex-shrink: 0;
                `,
            },
            img: {
                width: "86px",
                height: "86px",
                css: css`
                    height: auto;
                    width: auto;
                    max-height: 100%;
                    overflow: hidden;
                `,
            },
        },

        productBrandStyles: {
            nameLink: {
                typographyProps: {
                    size: 10,
                    css: css`
                        letter-spacing: 0;
                        line-height: 11px;
                    `,
                },
            },
        },
        productPartNumbersStyles: {
            container: {
                css: css`
                    margin-bottom: 8px;
                `,
            },
        },
        signInButton: {
            css: css`
                ${xScreen("lte", "xs")} {
                    width: 100%;
                }
            `,
        },
        productActionsStyles: {
            productAddToCartButtonStyles: {
                css: css`
                    width: 53px;
                    min-height: 30px;
                    border: none;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                `,
            },
            productQuantityOrderedStyles: {
                cssOverrides: {
                    formField: css``,
                    inputSelect: css`
                        text-align: center;
                        width: 43px;
                        height: 30px;
                        font-size: 14px;
                        letter-spacing: 0;
                        font-weight: 500;
                        line-height: 19px;
                        padding: 0;
                        &:focus {
                            padding: 0;
                            border: 1px solid ${color("common.ui9")};
                        }
                        &:hover {
                            border: 1px solid ${color("common.ui8")};
                        }
                    `,
                },
            },
            addToCartContainer: {
                css: css`
                    ${xScreen("gt", "sm")} {
                        justify-content: flex-end;
                    }
                `,
            },
        },
    },
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }
    render() {
        const { products, ...otherProps } = this.props;
        const styles = this.styles;
        const lastProductIndex = products.length - 1;
        return (
            <>
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography
                        {...styles.headerText}
                        data-test-selector="autocompleteProducts_Header"
                        className="autocomplete-products-header"
                    >
                        {translate("Recommended Products for")}
                        <Typography variant="h4" ellipsis={true} data-test-selector="autocompleteProducts_Header">
                            {this.props.query}
                        </Typography>
                    </Typography>
                    <Link
                        {...styles.viewAllLink}
                        onClick={() => {
                            this.props.doSearch?.();
                            this.props.onClickEvent?.("viewall_autocomplete", "/search");
                        }}
                        data-test-selector="search-view-all"
                        aria-label="view all"
                        id="viewall_autocomplete"
                    >
                        {translate("View All")}
                    </Link>
                </StyledWrapper>
                <StyledWrapper {...styles.productStyles?.container}>
                    {products.map((productContext, index) => (
                        <AutoCompleteProductCard
                            key={productContext.product.id}
                            index={index}
                            productContext={productContext}
                            lastProductIndex={lastProductIndex}
                            {...otherProps}
                        />
                    ))}
                </StyledWrapper>
            </>
        );
    }
}

export default connect(mapStateToProps)(AutocompleteProducts);

// XNG-Changes

interface AutoCompleteProductCardProps extends Omit<OwnProps, "products"> {
    productContext: ProductContextModel;
    index: number;
    lastProductIndex: number;
}
const AutoCompleteProductCard = ({
    productContext,
    index,
    lastProductIndex,
    onClickEvent,
    ...otherProps
}: AutoCompleteProductCardProps) => {
    const { showOverlayBadge, overlayBadges, overlayBadgesWrapperStyles } = xUseOverlayBadge({
        productContext,
        showTextBadge: true,
        showImageBadge: true,
    });

    return (
        <ProductContext.Provider value={productContext}>
            <StyledWrapper
                {...styles.productStyles?.productWrapper}
                className={`${index === lastProductIndex ? "last-item" : ""}`}
            >
                <StyledWrapper
                    css={css`
                        position: relative;
                        ${styles.productStyles?.productImage?.linkWrappingImage?.css}
                        flex-shrink: unset;
                    `}
                >
                    <ProductImage
                        product={productContext}
                        extendedStyles={styles.productStyles?.productImage}
                        // XNG-Changes: GA4 AutoComplete Event
                        onClick={() => onClickEvent?.("pdp_autocomplete", productContext.productInfo.productDetailPath)}
                    />
                    <OverlayBadges badges={overlayBadges} wrapperCss={overlayBadgesWrapperStyles} maxLength={1} />
                </StyledWrapper>
                <StyledWrapper {...styles.productStyles?.productContainer}>
                    <StyledWrapper {...styles.productStyles?.productInfoContainer}>
                        <ProductBrand
                            brand={productContext.product?.brand as BrandModel}
                            showLogo={false}
                            extendedStyles={mergeWithCss(styles.productStyles?.productBrandStyles, {
                                nameLink: {
                                    onClick: () => hideAll(),
                                },
                            })}
                        />
                        <ProductDescription
                            product={productContext}
                            // XNG-Changes: GA4 AutoComplete Event
                            onClick={() =>
                                onClickEvent?.("pdp_autocomplete", productContext.productInfo.productDetailPath)
                            }
                            extendedStyles={mergeWithCss(styles.productStyles?.productDescriptionStyles, {
                                productDetailLink: {
                                    onClick: () => hideAll(),
                                },
                            })}
                        />
                        <ProductPartNumbers
                            productNumber={productContext.product.productNumber}
                            manufacturerItem={productContext.product.manufacturerItem}
                            uniqueProductCode={productContext.product.detail?.upcCode}
                            extendedStyles={styles.productStyles?.productPartNumbersStyles}
                            customerProductNumber={productContext.product.customerProductNumber}
                        />
                        <StyledWrapper>
                            <StyledWrapper {...styles.productStyles?.priceWrapper}>
                                {otherProps.isPriceLoaded && (
                                    <ProductPrice showInvalidPriceMessage={true} product={productContext} />
                                )}
                                {!otherProps.isPriceLoaded && <ProductPrice product={productContext} />}
                            </StyledWrapper>
                            {!otherProps.isInventoryLoaded && <ProductContextAvailability />}
                            {otherProps.isInventoryLoaded && <ProductContextAvailability />}
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.productStyles?.actionsContainer}>
                        {otherProps.isPriceLoaded &&
                            otherProps.isInventoryLoaded &&
                            otherProps.availableForPurchase &&
                            otherProps.availableForPurchase[index] && (
                                <ProductActions
                                    iconOnly={true}
                                    showActions={false}
                                    showDisabled={false}
                                    extendedStyles={styles.productStyles?.productActionsStyles}
                                    hideUOM={true}
                                />
                            )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </ProductContext.Provider>
    );
};
